import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { WithdrawalSwapQuoteQuery } from 'src/generated/graphql';
import { baseToHuman } from 'src/pages/dashboard/swap/utils';

type Quote = WithdrawalSwapQuoteQuery['withdrawalSwapQuote'];

type QuotePanelProps = {
  quote: Quote | undefined;
  fetching: boolean;
  errorMessage: string | null;
};

const usd = (v: string | null | undefined) =>
  v == null
    ? '-'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        parseFloat(v) || 0
      );

const Line = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

/** Live quote for a withdrawal received as another asset. */
export function WithdrawalSwapQuotePanel({
  quote,
  fetching,
  errorMessage,
}: QuotePanelProps) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (errorMessage) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {errorMessage}
      </Alert>
    );
  }
  if (!quote) {
    return (
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {fetching && <CircularProgress size={16} />}
        <Typography variant="body2" color="text.secondary">
          {fetching ? 'Getting a quote...' : 'Enter an amount to see a quote.'}
        </Typography>
      </Box>
    );
  }

  const { destinationAsset: asset } = quote;
  const out = (atomic: string) =>
    `${baseToHuman(atomic, asset.decimals, asset.decimals)} ${asset.symbol}`;
  const secondsLeft = Math.max(0, Math.round((new Date(quote.deadline).getTime() - now) / 1000));

  return (
    <Stack spacing={0.5} sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Line label="You receive (estimate)" value={`${out(quote.quotedAmountOut)} (${usd(quote.quotedOutUsd)})`} />
      <Line label="Minimum you receive" value={out(quote.minAmountOut)} />
      <Line label="Sending" value={usd(quote.sourceUsd)} />
      <Typography variant="caption" color={secondsLeft === 0 ? 'error' : 'text.secondary'}>
        {secondsLeft === 0
          ? fetching
            ? 'Quote expired, refreshing...'
            : 'Quote expired'
          : `Quote refreshes automatically. Expires in ${secondsLeft}s.`}
      </Typography>
    </Stack>
  );
}
